// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { navigationConfig, NavCategory } from '../../config/navigationConfig';
import { hasPermission, RoleType, getRoleInfo } from '../../config/rolesConfig';
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { isDarkMode, toggleTheme } = useThemeStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
  };

  // Filter navigation based on user role permissions
  const filteredNavigation = useMemo((): NavCategory[] => {
    const userRole = (user?.role || 'viewer') as RoleType;

    return navigationConfig
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => {
          // If no permission required, allow access
          if (!item.permission) return true;
          // Check if user has required permission
          return hasPermission(userRole, item.permission);
        }),
      }))
      .filter((category) => category.items.length > 0); // Remove empty categories
  }, [user?.role]);

  // Get role display info
  const roleInfo = useMemo(() => {
    const userRole = (user?.role || 'viewer') as RoleType;
    return getRoleInfo(userRole);
  }, [user?.role]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-100'}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white dark:bg-gray-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b dark:border-gray-700 bg-teal-600">
            <Link to="/dashboard" className="flex items-center gap-2 text-xl font-bold text-white">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Auxilio Logistics
            </Link>
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            {filteredNavigation.map((category) => (
              <div key={category.name} className="mb-4">
                <h3 className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {category.name}
                </h3>
                <div className="mt-2 space-y-1">
                  {category.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center px-4 py-2 text-sm font-medium ${
                          isActive
                            ? 'text-teal-600 bg-teal-50 dark:bg-teal-900/50 dark:text-teal-400 border-r-2 border-teal-600'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Copyright Notice */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center font-light">
              Copyright 2026, Auxilio Technologies LLC
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="w-6 h-6 text-gray-500" />
            </button>

            <div className="flex items-center gap-4">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isDarkMode ? (
                  <SunIcon className="w-5 h-5 text-gray-400" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {/* User menu */}
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-end">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {user?.email}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full bg-${roleInfo.color}-100 text-${roleInfo.color}-700 dark:bg-${roleInfo.color}-900/30 dark:text-${roleInfo.color}-400`}>
                    {roleInfo.name}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={t('auth.logout')}
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
